import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { authApi, GoogleSignInError } from '@/lib/api/auth';
import { loadGoogleIdentity } from '@/lib/googleIdentity';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function GoogleSignIn({
  invite,
  disabled = false,
  onBusyChange,
}: {
  invite?: string | null;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
}) {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const submitting = useRef(false);
  const blocked = useRef(disabled);
  const [enabled, setEnabled] = useState(true);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [credential, setCredential] = useState('');
  const [password, setPassword] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    blocked.current = disabled;
  }, [disabled]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const signIn = useCallback(
    async (token: string, currentPassword?: string) => {
      if (submitting.current || blocked.current) return;
      submitting.current = true;
      setBusy(true);
      onBusyChange?.(true);
      setError('');
      try {
        const created = await googleLogin(token, currentPassword);
        if (mounted.current) {
          navigate(
            invite
              ? `/join-household?code=${invite}`
              : created
                ? '/onboarding'
                : '/dashboard'
          );
        }
      } catch (err) {
        if (!mounted.current) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Google sign-in failed. Please try again.'
        );
        setCredential(
          err instanceof GoogleSignInError && err.code === 'password_required'
            ? token
            : ''
        );
        setPassword('');
      } finally {
        submitting.current = false;
        if (mounted.current) {
          setBusy(false);
          onBusyChange?.(false);
        }
      }
    },
    [googleLogin, navigate, invite, onBusyChange]
  );

  useEffect(() => {
    let active = true;
    const element = container.current;
    const initialize = async () => {
      try {
        const config = await authApi.getGoogleSignInConfig();
        if (!active) return;
        setEnabled(config.enabled);
        if (!config.enabled) return;
        const google = await loadGoogleIdentity();
        if (!active || !element) return;
        google.initialize({
          client_id: config.client_id,
          nonce: config.nonce,
          auto_select: false,
          callback: response => {
            if (active) void signIn(response.credential);
          },
        });
        element.replaceChildren();
        google.renderButton(element, {
          type: 'standard',
          theme: document.documentElement.classList.contains('dark')
            ? 'filled_black'
            : 'outline',
          size: 'large',
          text: 'continue_with',
          width: Math.min(element.clientWidth || 320, 400),
        });
        setReady(true);
      } catch (err) {
        if (active)
          setError(
            err instanceof Error
              ? err.message
              : 'Google sign-in is unavailable.'
          );
      }
    };
    void initialize();
    return () => {
      active = false;
      element?.replaceChildren();
    };
  }, [signIn, attempt]);

  if (!enabled) return null;

  return (
    <div
      className="mb-6 space-y-4"
      aria-label="Google sign-in"
      aria-busy={busy}
    >
      {/* Match the Google iframe's canvas scheme so its padding stays transparent. */}
      <div
        ref={container}
        inert={busy || disabled || !!credential}
        className="flex min-h-11 w-full justify-center [color-scheme:light]"
      />
      {!ready && !error && (
        <p role="status" className="text-center text-sm text-muted-foreground">
          Loading Google sign-in…
        </p>
      )}
      {busy && (
        <p role="status" className="text-center text-sm text-muted-foreground">
          Signing in with Google…
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {credential ? (
        <form
          className="space-y-3"
          onSubmit={event => {
            event.preventDefault();
            void signIn(credential, password);
          }}
        >
          <Label htmlFor="google-link-password">Richtato password</Label>
          <Input
            id="google-link-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            required
            disabled={busy || disabled}
          />
          <Button type="submit" className="w-full" disabled={busy || disabled}>
            Connect Google and sign in
          </Button>
          <Link
            to="/forgot-password"
            className="block text-center text-sm text-primary hover:underline"
          >
            Forgot password?
          </Link>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setCredential('');
              setPassword('');
              setError('');
            }}
          >
            Use another Google account
          </Button>
        </form>
      ) : error ? (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={busy || disabled}
          onClick={() => {
            setError('');
            setReady(false);
            setAttempt(value => value + 1);
          }}
        >
          Try Google again
        </Button>
      ) : null}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        <span>or continue with a password</span>
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}
